'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var debug = _interopDefault(require('debug'));
var asosWebRequest = require('@asosteam/asos-web-request');
var agentkeepalive = require('agentkeepalive');
var configureLogger = _interopDefault(require('@asosteam/asos-web-logging'));

const errors = {
  StoreServiceError: "StoreServiceError",
  StoreFormatError: "StoreFormatError",
  StoreNotFoundError: "StoreNotFoundError",
  CacheError: "CacheError",
  InvalidArgumentError: "InvalidArgumentError"
};
const storeServiceError = (message, e) => ({
  type: errors.StoreServiceError,
  message,
  raw: e
});
const storeFormatError = message => ({
  type: errors.StoreFormatError,
  message
});
const cacheError = message => ({
  type: errors.CacheError,
  message
});
const invalidArgumentError = message => ({
  type: errors.InvalidArgumentError,
  message
});

const cacheFactory = {
  create: params => {
    const {
      expiryMs
    } = params || {};
    if (typeof expiryMs !== "number" || isNaN(expiryMs)) {
      throw cacheError("Invalid argument: Cache expiry (argument name: expiryMs) must be a valid number");
    }
    const logger = debug("@asosteam/asos-web-store-presentation-node:cache");
    const storage = new Map();
    return {
      set(key, value) {
        storage.set(key, {
          value,
          expiry: Date.now() + expiryMs
        });
        return value;
      },
      hasKey(key) {
        return storage.has(key);
      },
      hasExpired(key) {
        const entry = storage.get(key);
        return entry && entry.expiry < Date.now();
      },
      get(key) {
        if (!this.hasKey(key)) {
          logger(`Key missing: ${key}`);
          return null;
        }
        const entry = storage.get(key);
        if (this.hasExpired(key)) {
          logger(`Key expired: ${key}`);
        }
        return entry.value;
      }
    };
  }
};

var name = "@asosteam/asos-web-store-presentation-node";
var version = "11.0.2";

var logger = configureLogger({
  moduleName: name
});

const httpsAgent = new agentkeepalive.HttpsAgent();
const requestService = asosWebRequest.configureRequest({
  client: {
    name,
    version
  },
  httpsAgent,
  logger
});
async function request(url) {
  let response;
  try {
    response = await requestService.get({
      url
    });
  } catch (error) {
    const status = error.response && error.response.status;
    const information = error.response && error.response.data;
    throw storeServiceError("Failed to fetch", {
      url,
      status,
      information,
      errorResponse: error.message
    });
  }
  return response;
}

const ENVIRONMENT = {
  PRODUCTION: "production",
  E2E01: "e2e01",
  ENV02PT: "env02pt",
  SHARED: "shared",
  WEBINT: "webint",
  TEST287: "287test"
};

function generateApiUrl({
  environment,
  path,
  searchParams = {}
}) {
  const isProduction = environment === ENVIRONMENT.PRODUCTION;
  const baseUrl = isProduction ? "https://api.asos.com" : "https://api.asosservices.com";
  const url = `${baseUrl}${path}`;
  const definedSearchParams = Object.entries(searchParams).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
  const search = new URLSearchParams(definedSearchParams);
  if (!isProduction) {
    search.set("x-site-origin", `${environment}.asosdevelopment.com`);
  }
  if ([...search.values()].length > 0) {
    return `${url}?${search.toString()}`;
  }
  return url;
}

/*
 * @typedef {Object} Shop
 * @property {string} id - The unique identifier for the shop (e.g., "ASOS").
 * @property {string} name - The domain name of the shop (e.g., "www.asos.com").
 * @property {string} description - A brief description of the shop.
 */

/*
 * @returns {Promise<Shop[]>} A promise that resolves to an array of shop objects.
 */
function getShops(initialParams) {
  return async function (callParams) {
    const params = Object.assign({}, initialParams, callParams);
    const {
      cache,
      environment,
      warn
    } = params;
    const shopsUrl = generateApiUrl({
      path: "/fashion/store/v2/shops",
      environment
    });
    if (cache.hasKey(shopsUrl) && !cache.hasExpired(shopsUrl)) {
      return cache.get(shopsUrl);
    }
    try {
      const {
        data
      } = await request(shopsUrl);
      if (!data) {
        throw new Error("No shops received");
      }
      return cache.set(shopsUrl, data);
    } catch (error) {
      warn("Store presentation failed to fetch shops", shopsUrl, error);
      if (cache.hasKey(shopsUrl)) {
        warn("Store presentation forced to return a stale shops cache", error);
        warn("Resetting the stale cache");
        return cache.set(shopsUrl, cache.get(shopsUrl));
      }
      throw storeServiceError("Could not obtain available shops", error);
    }
  };
}

function buildVersionManifestUrl({
  environment,
  shop
}) {
  const versionManifestBaseUri = "https://assets.asosservices.com/asos-ui/manifest/";
  if (environment === "production") {
    return `${versionManifestBaseUri}${shop}/manifest.json`;
  }
  return `${versionManifestBaseUri}${shop}/manifest-${environment}.json`;
}

function getShop(potentialExpressRequest) {
  return potentialExpressRequest && typeof potentialExpressRequest.get === "function" ? potentialExpressRequest.get("asos-shop") || "asos" : "asos";
}

/*
 * @typedef {Object} SiteChromeVersion
 * @property {boolean} useCurrent
 * @property {string} current
 * @property {string} previous
 */

/*
 * @typedef {Object} VersionManifest
 * @property {string[]} permittedKeyStoreDataversions
 * @property {boolean} useCurrentKeyStoreDataversion
 * @property {{ current: string, previous: string }} keyStoreDataversion
 * @property {string[]} permittedSiteChromeVersions
 * @property {Object.<string, SiteChromeVersion>} siteChromeVersion
 */

/*
 * @returns {VersionManifest} versionManifest
 */
function getVersionManifest(initialParams) {
  return async function (callParams) {
    const params = Object.assign({}, initialParams, callParams);
    const {
      cache,
      environment,
      request: req,
      warn
    } = params;
    const shop = getShop(req);
    const versionManifestEndpoint = buildVersionManifestUrl({
      environment,
      shop
    });
    if (cache.hasKey(versionManifestEndpoint) && !cache.hasExpired(versionManifestEndpoint)) {
      return cache.get(versionManifestEndpoint);
    }
    try {
      const {
        data
      } = await request(versionManifestEndpoint);
      return cache.set(versionManifestEndpoint, data);
    } catch (error) {
      warn("Store presentation failed to fetch valid manifest from", versionManifestEndpoint, error);
      if (cache.hasKey(versionManifestEndpoint)) {
        warn("Store presentation forced to return a stale manifest cache", error);
        warn("Resetting the stale cache");
        return cache.set(versionManifestEndpoint, cache.get(versionManifestEndpoint));
      }
      throw storeServiceError("Could not obtain a valid manifest", error);
    }
  };
}

const oneYearInSeconds = 365 * 24 * 60 * 60;
const getCookieValue = (req, cookieName) => {
  return req.cookies[cookieName] || null;
};
const setCookieValue = (res, cookieName, value, options = {
  maxAge: oneYearInSeconds
}) => {
  res.cookie(cookieName, value, options);
};
const findKeyValuePair = (values, key) => {
  return values ? values.split("&").filter(keyValue => keyValue.includes(`${key}=`))[0] : null;
};
const concatenateKeyValuePairs = (cookieValue, newKey, newValue) => {
  if (cookieValue) {
    const keyValueInCookie = findKeyValuePair(cookieValue, newKey);
    if (keyValueInCookie) {
      return cookieValue.replace(keyValueInCookie, `${newKey}=${newValue}`);
    } else {
      return `${cookieValue}&${newKey}=${newValue}`;
    }
  }
  return `${newKey}=${newValue}`;
};
const setKeyedCookieValue = (req, res, cookieName, key, value, options) => {
  setCookieValue(res, cookieName, concatenateKeyValuePairs(getCookieValue(req, cookieName), key, value), options);
};
const getKeyedCookieValue = (req, cookieName, key) => {
  const keyValuePair = findKeyValuePair(getCookieValue(req, cookieName), key);
  return keyValuePair ? keyValuePair.split("=")[1] : null;
};

const getGenderByFloorId = floorId => {
  switch (Number(floorId)) {
    case 1000:
      return "women";
    case 1001:
      return "men";
    default:
      return "default";
  }
};
const getFloorIdByGender = gender => {
  if (typeof gender !== "string") {
    return null;
  }
  switch (gender.toLowerCase()) {
    case "women":
      return 1000;
    case "men":
      return 1001;
    default:
      return null;
  }
};

var cookiesMap = {
  BrowseCountryKey: "browseCountry",
  FloorCookieName: "floor",
  AsosCookieName: "asos",
  CurrencyCookieName: "currency",
  CurrencyIdKey: "currencyid",
  CurrencyLabelKey: "currencylabel",
  SizeSchemaKey: "sizeSchema",
  PreferredSiteKey: "PreferredSite",
  TopCatIdKey: "topcatid",
  BrowseCurrencyKey: "browseCurrency",
  BrowseLanguageKey: "browseLanguage",
  BrowseSizeSchemaKey: "browseSizeSchema",
  StoreCodeKey: "storeCode",
  TemplateVersionKey: "siteChromeVersion",
  KeyStoreDataversionKey: "keyStoreDataversion",
  GeoCountryKey: "geocountry",
  AffiliateCookieName: "asosAffiliate",
  AffiliateKey: "affiliateId",
  XSiteOrigin: "x-site-origin",
  CtaRef: "ctaRef"
};

const oneHourInSeconds = 60 * 60;
const {
  FloorCookieName,
  AsosCookieName,
  SizeSchemaKey,
  PreferredSiteKey,
  TopCatIdKey,
  BrowseCountryKey,
  KeyStoreDataversionKey,
  TemplateVersionKey,
  BrowseCurrencyKey,
  BrowseSizeSchemaKey,
  BrowseLanguageKey,
  XSiteOrigin
} = cookiesMap;
const getSizeSchema = req => getKeyedCookieValue(req, AsosCookieName, SizeSchemaKey);
const setSizeSchema = (req, res, sizeSchema) => {
  if (!sizeSchema) {
    throw invalidArgumentError("Invalid argument: sizeSchema cannot be empty");
  }
  setKeyedCookieValue(req, res, AsosCookieName, SizeSchemaKey, sizeSchema);
};
const getFloor = req => getGenderByFloorId(getCookieValue(req, FloorCookieName));
const setFloor = (req, res, gender) => {
  if (!gender) {
    throw invalidArgumentError("Invalid argument: gender cannot be empty");
  }
  const floorId = getFloorIdByGender(gender);
  if (floorId) {
    setCookieValue(res, FloorCookieName, floorId);
    setKeyedCookieValue(req, res, AsosCookieName, TopCatIdKey, floorId);
  }
};
const getPreferredSite = req => getKeyedCookieValue(req, AsosCookieName, PreferredSiteKey);
const isFirstVisit = req => !getPreferredSite(req);
const getBrowseCountry = req => getCookieValue(req, BrowseCountryKey);
const clearCookies = res => {
  res.clearCookie(AsosCookieName);
  res.clearCookie(FloorCookieName);
};
const getKeyStoreDataversion = req => getCookieValue(req, KeyStoreDataversionKey);
const setKeyStoreDataversion = (res, value) => setCookieValue(res, KeyStoreDataversionKey, value, {
  maxAge: oneHourInSeconds
});
const getTemplateVersion = (req, storeCode) => getKeyedCookieValue(req, TemplateVersionKey, storeCode);
const setTemplateVersion = (req, res, storeCode, value) => setKeyedCookieValue(req, res, TemplateVersionKey, storeCode, value, {
  maxAge: oneHourInSeconds
});
const getBrowseCurrency = req => [req.query[BrowseCurrencyKey] || null, getCookieValue(req, BrowseCurrencyKey)].find(currencyCode => currencyCode !== null) || null;
const getBrowseSizeSchema = req => getCookieValue(req, BrowseSizeSchemaKey);
const getBrowseLanguage = req => getCookieValue(req, BrowseLanguageKey);
const getXSiteOriginCookieValue = req => getCookieValue(req, XSiteOrigin);

function getKeyStoreDataversion$1(initialParams) {
  return async function (callParams) {
    const params = Object.assign({}, initialParams, callParams);
    const {
      getVersionManifest,
      request
    } = params;
    let ksdvInCookie = null;
    if (request) {
      ksdvInCookie = getKeyStoreDataversion(request);
      if (ksdvInCookie) {
        return ksdvInCookie;
      }
    }
    const {
      useCurrentKeyStoreDataversion,
      keyStoreDataversion
    } = await getVersionManifest({
      request
    });
    return useCurrentKeyStoreDataversion ? keyStoreDataversion.current : keyStoreDataversion.previous;
  };
}

/*
 * @returns {string} siteChromeVersion
 */
function getSiteChromeVersion(initialParams) {
  return async function (callParams) {
    const params = Object.assign({}, initialParams, callParams);
    const {
      getCountry,
      getVersionManifest,
      request,
      response
    } = params;
    const {
      storeCode
    } = await getCountry({
      request,
      response
    });
    const siteChromeVersionInCookie = getTemplateVersion(request, storeCode.toLowerCase());
    const {
      siteChromeVersion,
      permittedSiteChromeVersions
    } = await getVersionManifest({
      request
    });
    if (!siteChromeVersion) {
      throw storeServiceError("Could not get valid version manifest");
    }
    if (siteChromeVersionInCookie && permittedSiteChromeVersions.some(value => {
      return value === "*" || value === siteChromeVersionInCookie;
    })) {
      return siteChromeVersionInCookie;
    }
    const storesSiteChromeVersion = siteChromeVersion[storeCode] || siteChromeVersion[storeCode.toLowerCase()] || null;
    if (storesSiteChromeVersion === null) {
      throw storeServiceError("Could not match store in the version manifest");
    }
    const activeSiteChrome = storesSiteChromeVersion.useCurrent ? storesSiteChromeVersion.current : storesSiteChromeVersion.previous;

    // If cookie is set but isn't permitted
    if (siteChromeVersionInCookie && request && response) {
      setTemplateVersion(request, response, activeSiteChrome);
    }
    return activeSiteChrome;
  };
}

const DEFAULT_STORE_MAP = {
  asos: "COM",
  topshop: "TS-GB",
  default: "COM"
};
function getDefaultStoreCodeForShop(shop) {
  const defaultStoreCode = DEFAULT_STORE_MAP[shop.toLowerCase()] || DEFAULT_STORE_MAP.default;
  return defaultStoreCode;
}

//subdomain.domain.com/{2-letter country code}
const STORES_REGEX = /([a-zA-Z]{2,6})\.[a-zA-Z]{1,}\.com\/?([a-zA-Z]{2})?/;
const getFallbackCountry = function (shop, defaultCountries) {
  const defaultStoreCode = getDefaultStoreCodeForShop(shop);
  return defaultCountries.find(country => defaultStoreCode === country.storeCode);
};
const getStoreCodeFromXSiteUrn = function (xSiteUrn) {
  const storeRegexMatches = xSiteUrn.match(STORES_REGEX);
  if (!storeRegexMatches) {
    return null;
  }
  if (storeRegexMatches[2]) {
    return storeRegexMatches[2];
  } else {
    return null;
  }
};
const getCountryByStoreCode = function (defaultCountries, storeCode) {
  if (!storeCode) {
    return null;
  }
  return defaultCountries.find(country => country.storeCode.toLowerCase() === storeCode.toLowerCase());
};
function getDefaultCountry(req, defaultCountries) {
  const shop = getShop(req);
  const fallbackCountry = getFallbackCountry(shop, defaultCountries);
  const xSiteUrn = req.get("X-SITE-URN");
  if (!xSiteUrn) {
    return fallbackCountry;
  }
  const storeCode = getStoreCodeFromXSiteUrn(xSiteUrn);
  return getCountryByStoreCode(defaultCountries, storeCode) || fallbackCountry;
}

const getBrowseCountryFromQueryString = req => typeof req.query === "object" && typeof req.query.browseCountry === "string" && req.query.browseCountry || typeof req.query === "object" && typeof req.query.browsecountry === "string" && req.query.browsecountry;
const getBrowseCountryFromCookie = req => req.cookies && getBrowseCountry(req);
function getBrowseCountry$1(req) {
  return getBrowseCountryFromQueryString(req) || getBrowseCountryFromCookie(req) || "";
}

const lc = str => str && str.toLowerCase();
const getIsCountryValidForDomain = (country, defaultCountry, defaultCountries) => {
  const validStoreCodes = defaultCountries.filter(country => country.url === defaultCountry.url).map(country => country.storeCode);
  return validStoreCodes.includes(country.storeCode);
};

/*
 * @returns {Country} country
 */
function getCountry(initialParams) {
  return async function (callParams) {
    const params = Object.assign({}, initialParams, callParams);
    const {
      getCountryCollections,
      request,
      response,
      lang
    } = params;
    const {
      majorCountries,
      defaultCountries
    } = await getCountryCollections({
      request,
      response,
      lang
    });
    const defaultCountry = getDefaultCountry(request, defaultCountries);
    const browseCountry = lc(getBrowseCountry$1(request));
    if (!browseCountry) {
      return defaultCountry;
    }
    const candidateCountry = majorCountries.find(country => {
      return lc(country.countryCode) === browseCountry;
    });
    if (!candidateCountry) {
      return defaultCountry;
    }
    if (!getIsCountryValidForDomain(candidateCountry, defaultCountry, defaultCountries)) {
      return defaultCountry;
    }
    return candidateCountry;
  };
}

const validEnvironments = Object.values(ENVIRONMENT);
function validate({
  warn,
  environment
}) {
  if (typeof warn !== "function") {
    throw storeServiceError("Invalid argument: warn (must be a function)");
  }
  if (!validEnvironments.find(env => env === environment)) {
    throw storeServiceError(`Invalid argument: environment (must be one of: ${validEnvironments.join(", ")})`);
  }
  return true;
}

/*
 * @returns {Country[]} majorCountries
 */
function getMajorCountries(initialParams) {
  return async function (callParams) {
    const params = Object.assign({}, initialParams, callParams);
    const {
      getCountryCollections,
      request,
      response,
      lang
    } = params;
    const {
      majorCountries
    } = await getCountryCollections({
      request,
      response,
      lang
    });

    // FIXME: majorCountries should never be null or undefined as
    // getCountryCollections will throw an error beforehand
    if (majorCountries) {
      return majorCountries;
    } else {
      throw storeServiceError("Major countries was not calculated correctly");
    }
  };
}

function enrichMinorCountries(countries) {
  const countryMap = countries.reduce((memo, next) => {
    return Object.assign(memo, {
      [next.country]: next
    });
  }, {});
  return countries.map(country => {
    if (country.majorCountry === null) {
      return country;
    }
    return looseAssign(countryMap[country.majorCountry], country);
  });
}
function looseAssign({
  store,
  siteUrl,
  siteId,
  languages,
  currencies,
  sizeSchemas
}, minor) {
  return Object.assign(minor, {
    store,
    siteUrl,
    siteId,
    languages,
    currencies,
    sizeSchemas
  });
}

const getPrimary = ({
  list,
  errorMessage
}) => {
  const primaryItem = list.find(item => item.isPrimary);
  if (primaryItem) {
    return primaryItem;
  }
  throw storeFormatError(errorMessage);
};
const getPrimaryLanguage = store => getPrimary({
  list: store.languages,
  errorMessage: `No primary language defined for ${store.country} store`
}).language;
const getPrimaryCurrency = store => getPrimary({
  list: store.currencies,
  errorMessage: `No primary currency defined for ${store.country} store`
}).currency;
const getPrimarySizeSchema = store => getPrimary({
  list: store.sizeSchemas,
  errorMessage: `No primary size defined for ${store.country} store`
}).sizeSchema;

const formatCountries = (countries, keyStoreDataversion) => countries.map(country => format(country, keyStoreDataversion));

/*
 * @typedef {Object} Country
 * @property {string} siteId
 * @property {string} storeCode
 * @property {Object} defaultLanguage
 * @property {Object} defaultSizeSchema
 * @property {Object} defaultCurrency
 * @property {string} url
 * @property {string} countryCode
 * @property {string} imageUrl
 * @property {string} name
 * @property {string} majorCountry
 * @property {string} isDefaultCountry
 * @property {Object[]} currencies
 * @property {Object[]} languages
 * @property {Object[]} sizeSchemas
 * @property {string} keyStoreDataversion
 * @property {Object.<string, Country>} [keyStoreDataversionVariants]
 */

/*
 * @returns {Country} country
 */
function format(country, keyStoreDataversion) {
  return {
    siteId: country.siteId,
    storeCode: country.store,
    defaultLanguage: getPrimaryLanguage(country),
    defaultSizeSchema: getPrimarySizeSchema(country),
    defaultCurrency: getPrimaryCurrency(country),
    url: country.siteUrl,
    countryCode: country.country,
    imageUrl: country.imageUrl,
    name: country.name,
    majorCountry: country.majorCountry,
    isDefaultCountry: false,
    currencies: country.currencies,
    languages: country.languages,
    sizeSchemas: country.sizeSchemas,
    keyStoreDataversion: keyStoreDataversion
  };
}

function sanitiseStoreCodeAsCountryCode(storeCode) {
  // The store code could either be a two or three letter value e.g. GB or COM, or
  // two letters with - followed by a two or three letter value e.g. TS-GB or TS-COM
  // We want to return the value if no "-" or the right hand part e.g. GB
  // We are doing this as the store code is needing to be unique across all shops
  // so we can't reuse the same store code as the asos shop, therefore a prefix is
  // added to the store code to make it unique.
  // For example for topshop is TS-GB
  // As store code is also being used as a representation of the country code we need
  // to return the right hand part of the store code.
  const storeCodeParts = storeCode.split("-");
  if (storeCodeParts.length > 1) {
    return storeCodeParts[1];
  }
  return storeCode;
}

function getDefaultCountries(shop, countries) {
  const defaultStoreCode = getDefaultStoreCodeForShop(shop);
  const distinctStores = getDistinctStores(countries);
  const fallbackStoreCountry = countries.find(c => c.storeCode === defaultStoreCode);
  return distinctStores.map(({
    storeCode,
    storeId
  }) => {
    let country = countries.find(c => c.countryCode === sanitiseStoreCodeAsCountryCode(storeCode)) || fallbackStoreCountry;
    const store = {
      isDefaultCountry: true,
      storeCode,
      storeId,
      isXSiteUrnFallback: storeCode === fallbackStoreCountry.storeCode
    };
    return Object.assign({}, country, store);
  });
}
const getUniqueStores = (acc, country) => {
  if (!acc.find(existingItem => existingItem.storeCode === country.storeCode)) {
    acc.push({
      storeId: country.siteId,
      storeCode: country.storeCode
    });
  }
  return acc;
};
const getDistinctStores = countries => {
  return countries.reduce(getUniqueStores, []);
};

/*
 * @typedef {Object} CountryCollections
 * @property {Country[]} majorCountries
 * @property {Country[]} defaultCountries
 * @property {Country[]} countries
 */

/*
 * @retuns {CountryCollections} countryCollections
 */
function createCountryCollections(shop, countries) {
  const isMajorCountry = country => country.majorCountry === null;
  const majorCountries = countries.filter(isMajorCountry);
  const defaultCountries = getDefaultCountries(shop, majorCountries);
  return {
    majorCountries,
    defaultCountries,
    countries
  };
}

function getCountryCollections(initialParams) {
  return async function (callParams) {
    const params = Object.assign({}, initialParams, callParams);
    const {
      cache,
      getKeyStoreDataversion,
      warn,
      environment,
      request: expressRequest,
      lang
    } = params;
    const shop = getShop(expressRequest);
    const keyStoreDataversion = await getKeyStoreDataversion({
      request: expressRequest
    });
    const storeUrl = generateApiUrl({
      environment,
      path: `/fashion/store/v2/${shop}/stores/countries`,
      searchParams: {
        keyStoreDataversion,
        lang
      }
    });
    if (cache.hasKey(storeUrl) && !cache.hasExpired(storeUrl)) {
      return cache.get(storeUrl);
    }
    try {
      const {
        data
      } = await request(storeUrl);
      // we cache the response in the form
      // {
      //    majorCountries, defaultCountries, countries
      // }
      // where each of the country objects has already been enriched and formatted
      // enrichment is where minor countries are enriched with values from their major
      // formatted is where keys for each country are standardized and only relevant values
      // are kept. Finally we add the keyStoreDataversion for this request to the object.
      return cache.set(storeUrl, createCountryCollections(shop, formatCountries(enrichMinorCountries(data), keyStoreDataversion)));
    } catch (error) {
      warn("Store presentation failed to fetch fresh stores", error);
      if (cache.hasKey(storeUrl)) {
        warn("Store presentation forced to return stale stores cache", error);
        warn("Resetting the stale cache");
        return cache.set(storeUrl, cache.get(storeUrl));
      }
      throw storeServiceError("Could not obtain stores", error);
    }
  };
}

/*
 * @returns {Country[]} countries
 */
function getCountries(initialParams) {
  return async function (callParams) {
    const params = Object.assign({}, initialParams, callParams);
    const {
      getCountryCollections,
      request,
      response,
      lang
    } = params;
    const {
      countries
    } = await getCountryCollections({
      request,
      response,
      lang
    });

    // FIXME: countries should never be null or undefined as
    // getCountryCollections will throw an error beforehand
    if (countries) {
      return countries;
    } else {
      throw storeServiceError("Countries was not calculated correctly");
    }
  };
}

/*
 * @returns {Country[]} defaultCountries
 */
function getDefaultCountries$1(initialParams) {
  return async function (callParams) {
    const params = Object.assign({}, initialParams, callParams);
    const {
      getCountryCollections,
      request,
      response,
      lang
    } = params;
    const {
      defaultCountries
    } = await getCountryCollections({
      request,
      response,
      lang
    });

    // FIXME: defaultCountries should never be null or undefined as
    // getCountryCollections will throw an error beforehand
    if (defaultCountries) {
      return defaultCountries;
    } else {
      throw storeServiceError("Default countries was not calculated correctly");
    }
  };
}

/*
 * @returns {Country[]} majorCountries
 */
function getMajorCountriesWithKSDVVariants(initialParams) {
  return async function (callParams) {
    const params = Object.assign({}, initialParams, callParams);
    const {
      getCountryCollections,
      getVersionManifest,
      getKeyStoreDataversion,
      request,
      response,
      lang,
      warn
    } = params;
    try {
      const {
        permittedKeyStoreDataversions
      } = await getVersionManifest({
        request
      });
      const currentKSDV = await getKeyStoreDataversion({
        request,
        response
      });
      const countriesByKSDV = {};
      for (let ksdv of permittedKeyStoreDataversions) {
        try {
          const {
            majorCountries
          } = await getCountryCollections({
            request,
            response,
            lang,
            getKeyStoreDataversion: () => Promise.resolve(ksdv)
          });
          if (majorCountries && majorCountries.length) {
            countriesByKSDV[ksdv] = majorCountries.slice();
          }
        } catch (e) {
          warn(`Get country collection failed for KSDV: ${ksdv}`);
        }
      }
      const majorCountriesWithKSDV = countriesByKSDV[currentKSDV] || [];
      delete countriesByKSDV[currentKSDV];
      majorCountriesWithKSDV.forEach(country => {
        country.keyStoreDataversionVariants = Object.keys(countriesByKSDV).reduce((acc, ksdv) => {
          let foundIndex;
          let foundCountry = countriesByKSDV[ksdv].find((el, index) => {
            if (el.countryCode === country.countryCode) {
              foundIndex = index;
              return true;
            }
            return false;
          });
          if (foundCountry) {
            acc[ksdv] = foundCountry;
            countriesByKSDV[ksdv].splice(foundIndex, 1);
          }
          return acc;
        }, {});
      });
      return majorCountriesWithKSDV;
    } catch (e) {
      throw storeServiceError("Failed to getMajorCountriesWithKSDVVariants", e);
    }
  };
}

/*
 * @param {Country} country
 *
 * @returns {Currency} currency
 */
const getDefaultCurrencyForCountry = country => country.currencies.find(currency => currency.isPrimary);

/*
 * @param {string} browseCurrency
 * @param {Country} country
 *
 * @returns {Currency} currency
 */
const getCurrencyIfValid = (browseCurrency, country) => country.currencies.find(({
  currency
}) => browseCurrency === currency);

/*
 * @typedef {Object} Currency
 * @property {string} currency
 * @property {string} symbol
 * @property {string} text
 * @property {boolean} isPrimary
 * @property {number} currencyId
 */

/*
 * @typedef {Object} GetCurrencyOptions
 * @property {Function} getCountry
 * @property {Object} request
 * @property {Object} response
 */

/*
 * @async
 * @callback CurriedGetCurrency
 * @param {GetCurrencyOptions} options
 */

/*
 * @param {GetCurrencyOptions} initialParams
 *
 * @returns {CurriedGetCurrency} curriedGetCurrency
 */
function getCurrency(initialParams) {
  return async function (callParams) {
    const {
      request,
      response,
      getCountry
    } = Object.assign({}, initialParams, callParams);
    const country = await getCountry({
      request,
      response
    });
    const browseCurrency = getBrowseCurrency(request);
    const validCurrency = getCurrencyIfValid(browseCurrency, country);
    return validCurrency || getDefaultCurrencyForCountry(country);
  };
}

/*
 * @param {Country} country
 *
 * @returns {Language} language
 */
const getDefaultLanguageForCountry = country => country.languages.find(language => language.isPrimary);

/*
 * @param {string} browseLanguage
 * @param {Country} country
 *
 * @returns {Language} language
 */
const getLanguageIfValid = (browseLanguage, country) => country.languages.find(({
  language
}) => browseLanguage === language);

/*
 * @typedef {Object} Language
 * @property {string} language
 * @property {string} name
 * @property {string} text
 * @property {string} languageShort
 * @property {boolean} isPrimary
 */

/*
 * @typedef {Object} GetLanguageOptions
 * @property {Function} getCountry
 * @property {Object} request
 * @property {Object} response
 */

/*
 * @async
 * @callback CurriedGetLanguage
 * @param {GetLanguageOptions} options
 */

/*
 * @param {GetLanguageOptions} initialParams
 *
 * @returns {CurriedGetLanguage} curriedGetLanguage
 */
function getLanguage(initialParams) {
  return async function (callParams) {
    const {
      request,
      response,
      getCountry
    } = Object.assign({}, initialParams, callParams);
    const country = await getCountry({
      request,
      response
    });
    const browseLanguage = getBrowseLanguage(request);
    const validLanguage = getLanguageIfValid(browseLanguage, country);
    return validLanguage || getDefaultLanguageForCountry(country);
  };
}

/*
 * @param {Country} country
 *
 * @returns {SizeSchema} sizeSchema
 */
const getDefaultSizeSchemaForCountry = country => country.sizeSchemas.find(sizeSchema => sizeSchema.isPrimary);

/*
 * @param {string} browseSizeSchema
 * @param {Country} country
 *
 * @returns {SizeSchema} sizeSchema
 */
const getSizeSchemaIfValid = (browseSizeSchema, country) => country.sizeSchemas.find(({
  sizeSchema
}) => browseSizeSchema === sizeSchema);

/*
 * @typedef {Object} SizeSchema
 * @property {string} sizeSchema
 * @property {string} text
 * @property {boolean} isPrimary
 */

/*
 * @typedef {Object} GetSizeSchemaOptions
 * @property {Function} getCountry
 * @property {Object} request
 * @property {Object} response
 */

/*
 * @async
 * @callback CurriedGetSizeSchema
 * @param {GetSizeSchemaOptions} options
 */

/*
 * @param {GetSizeSchemaOptions} initialParams
 *
 * @returns {CurriedGetSizeSchema} curriedGetSizeSchema
 */
function getSizeSchema$1(initialParams) {
  return async function (callParams) {
    const {
      request,
      response,
      getCountry
    } = Object.assign({}, initialParams, callParams);
    const country = await getCountry({
      request,
      response
    });
    const browseSizeSchema = getBrowseSizeSchema(request);
    const validSizeSchema = getSizeSchemaIfValid(browseSizeSchema, country);
    return validSizeSchema || getDefaultSizeSchemaForCountry(country);
  };
}

function setProtocolToHttps(url) {
  const hasProtocol = url.indexOf("://") != -1;
  if (hasProtocol) {
    url = url.replace(/^https?:\/\//, "");
  }
  return "https://" + url;
}

var asos = {
	en: {
		women: "women",
		men: "men"
	},
	fr: {
		women: "femme",
		men: "homme"
	},
	de: {
		women: "damen",
		men: "herren"
	},
	it: {
		women: "donna",
		men: "uomo"
	},
	es: {
		women: "mujer",
		men: "hombre"
	},
	ru: {
		women: "women",
		men: "men"
	},
	sv: {
		women: "kvinna",
		men: "man"
	},
	nl: {
		women: "dames",
		men: "heren"
	},
	pl: {
		women: "kobiety",
		men: "mezczyzni"
	},
	da: {
		women: "kvinder",
		men: "maend"
	}
};
var topshop = {
	en: {
		women: "topshop",
		men: "topman"
	}
};
var genderTranslations = {
	asos: asos,
	topshop: topshop
};

const defaultLanguage = "en";
function modifyUrlForGender({
  shop,
  gender,
  languageShort,
  url
}) {
  if (!shop || !gender || !languageShort || !url) {
    return;
  }
  if (!genderTranslations[shop] || !genderTranslations[shop][languageShort]) {
    languageShort = defaultLanguage;
  }
  const translationForGender = genderTranslations[shop][languageShort][gender];
  const updatedUrl = url + "/" + translationForGender;
  return updatedUrl;
}

function generateSecureUrl({
  siteUrl
} = {}) {
  let secureUrl;
  if (siteUrl) {
    secureUrl = "https://" + siteUrl.replace(/^(www|test)\./, "secure.");
  } else {
    secureUrl = "https://secure.asos.com";
  }
  return secureUrl;
}

function getStoreUrlByCountryCode(initialParams) {
  return async function (callParams) {
    const params = Object.assign({}, initialParams, callParams);
    const {
      countryCode,
      keyStoreDataversion,
      type = "default",
      includeProtocol = false,
      cache,
      environment,
      request: req,
      warn
    } = params;
    if (!countryCode || !keyStoreDataversion) {
      throw storeServiceError("countryCode and keyStoreDataversion are required");
    }
    const shop = getShop(req);
    const storeUrl = generateApiUrl({
      environment,
      path: `/fashion/store/v2/${shop}/stores/countries`,
      searchParams: {
        keyStoreDataversion
      }
    });
    if (!cache.hasKey(storeUrl) || cache.hasExpired(storeUrl)) {
      try {
        const {
          data
        } = await request(storeUrl);
        cache.set(storeUrl, createCountryCollections(shop, formatCountries(enrichMinorCountries(data), keyStoreDataversion)));
      } catch (error) {
        warn("Store presentation failed to fetch fresh stores", error);

        /* Disabling this line, since it is tested but since it
         * throws istanbul doesn't detect it as tested */
        /* istanbul ignore next */
        if (!cache.hasKey(storeUrl)) {
          //Only throw the error if there is no cache available
          throw storeServiceError("Could not obtain stores", error);
        }
      }
    }
    let {
      majorCountries
    } = cache.get(storeUrl);
    const country = majorCountries.find(c => {
      return c.countryCode === countryCode;
    });
    let url = country.url;
    if (type === "secure") {
      return generateSecureUrl({
        siteUrl: url
      });
    }
    if (includeProtocol) {
      url = setProtocolToHttps(url);
    }
    if (type === "women" || type === "men") {
      const defaultLanguage = country.languages.find(language => language.isPrimary);
      const languageShort = defaultLanguage.languageShort;
      url = modifyUrlForGender({
        shop,
        gender: type,
        languageShort,
        url
      });
    }
    return url;
  };
}

const fiveMinutes = 1000 * 60 * 5;
const storeFactory = {
  create: ({
    environment,
    storeExpiryMs = fiveMinutes,
    versionManifestExpiryMs = fiveMinutes,
    warn
  } = {}) => {
    validate({
      environment,
      warn
    });
    const shopsCache = cacheFactory.create({
      expiryMs: storeExpiryMs
    });
    const storeCache = cacheFactory.create({
      expiryMs: storeExpiryMs
    });
    const versionManifestCache = cacheFactory.create({
      expiryMs: versionManifestExpiryMs
    });

    /*
     * Needs no parameters
     * @returns {Shop[]}
     */
    const curriedGetShops = getShops({
      cache: shopsCache,
      environment,
      warn
    });

    /*
     * Needs no parameters
     * @returns {VersionManifest}
     */
    const curriedGetVersionManifest = getVersionManifest({
      cache: versionManifestCache,
      environment,
      warn
    });

    /*
     * @param {Object} request
     *        Express request
     * @param {Object} response
     *        Express response
     * @returns {string} keyStoreDataversion
     */
    const curriedGetKeyStoreDataversion = getKeyStoreDataversion$1({
      getVersionManifest: curriedGetVersionManifest
    });

    /*
     * @param {Object} request
     *        Express request
     * @param {Object} response
     *        Express response
     * @param {string} lang
     * @param {Object} cache,
     * @param {Function} getKeyStoreDataversion
     * @returns {CountryCollections} countryCollections
     */
    const curriedGetCountryCollections = getCountryCollections({
      cache: storeCache,
      environment,
      getKeyStoreDataversion: curriedGetKeyStoreDataversion,
      warn
    });

    /*
     * @param {Object} request
     *        Express request
     * @param {Object} response
     *        Express response
     * @param {string} lang
     * @returns {Country[]} countries
     */
    const curriedGetCountries = getCountries({
      getCountryCollections: curriedGetCountryCollections
    });

    /*
     * @param {Object} request
     *        Express request
     * @param {Object} response
     *        Express response
     * @param {string} lang
     * @returns {Country[]} majorCountries
     */
    const curriedGetMajorCountries = getMajorCountries({
      getCountryCollections: curriedGetCountryCollections
    });

    /*
     * @param {Object} request
     *        Express request
     * @param {Object} response
     *        Express response
     * @param {string} lang
     * @returns {Country[]} defaultCountries
     */
    const curriedGetDefaultCountries = getDefaultCountries$1({
      getCountryCollections: curriedGetCountryCollections
    });

    /*
     * @param {Object} request
     *        Express request
     * @param {Object} response
     *        Express response
     * @param {string} lang
     * @returns {Country} country
     */
    const curriedGetCountry = getCountry({
      getCountryCollections: curriedGetCountryCollections
    });

    /*
     * @param {Object} request
     *        Express request
     * @param {Object} response
     *        Express response
     * @param {string} lang
     * @returns {Country[]} majorCountries
     */
    const curriedGetMajorCountriesWithKSDVVariants = getMajorCountriesWithKSDVVariants({
      getCountryCollections: curriedGetCountryCollections,
      getVersionManifest: curriedGetVersionManifest,
      getKeyStoreDataversion: curriedGetKeyStoreDataversion,
      warn
    });

    /*
     * @param {Object} request
     *        Express request
     * @param {Object} response
     *        Express response
     * @returns {string} siteChromeVersion
     */
    const curriedGetSiteChromeVersion = getSiteChromeVersion({
      getCountry: curriedGetCountry,
      getVersionManifest: curriedGetVersionManifest
    });

    /*
     * @param {Object} options
     * @param {Object} options.request
     * @param {Object} options.response
     */
    const curriedGetCurrency = getCurrency({
      getCountry: curriedGetCountry
    });

    /*
     * @param {Object} options
     * @param {Object} options.request
     * @param {Object} options.response
     */
    const curriedGetLanguage = getLanguage({
      getCountry: curriedGetCountry
    });

    /*
     * @param {Object} options
     * @param {Object} options.request
     * @param {Object} options.response
     */
    const curriedGetSizeSchema = getSizeSchema$1({
      getCountry: curriedGetCountry
    });

    /*
     * @param {Function} options.request
     */
    const curriedGetStoreUrlByCountryCode = getStoreUrlByCountryCode({
      cache: storeCache,
      environment,
      warn
    });
    return {
      getShops: curriedGetShops,
      getCountries: curriedGetCountries,
      getMajorCountries: curriedGetMajorCountries,
      getMajorCountriesWithKSDVVariants: curriedGetMajorCountriesWithKSDVVariants,
      getDefaultCountries: curriedGetDefaultCountries,
      getCountry: curriedGetCountry,
      getKeyStoreDataversion: curriedGetKeyStoreDataversion,
      getVersionManifest: curriedGetVersionManifest,
      getSiteChromeVersion: curriedGetSiteChromeVersion,
      getCurrency: curriedGetCurrency,
      getLanguage: curriedGetLanguage,
      getSizeSchema: curriedGetSizeSchema,
      getStoreUrlByCountryCode: curriedGetStoreUrlByCountryCode
    };
  }
};

const {
  XSiteOrigin: XSiteOrigin$1
} = cookiesMap;
const getXSiteOriginFromQueryString = req => typeof req.query === "object" && typeof req.query[XSiteOrigin$1] === "string" && req.query[XSiteOrigin$1];
function getXSiteOrigin(req) {
  return getXSiteOriginFromQueryString(req) || getXSiteOriginCookieValue(req);
}

const MOBILE = "mobile";
const TABLET = "tablet";
const DESKTOP = "desktop";
const isMobile = req => req.get("IsMobile") === "true";
const isTablet = req => req.get("IsTablet") === "true";
const isStopMobileSet = req => req.query.stop_mobi === "yes";
const getPlatform = req => {
  switch (true) {
    case isTablet(req):
      return TABLET;
    case isMobile(req):
      return MOBILE;
    default:
      return DESKTOP;
  }
};

const storePresentation = ({
  environment,
  storeExpiryMs,
  versionManifestExpiryMs,
  warn
} = {}) => ({
  store: storeFactory.create({
    environment,
    storeExpiryMs,
    versionManifestExpiryMs,
    warn
  })
});

exports.getXSiteOrigin = getXSiteOrigin;
exports.getShop = getShop;
exports.storePresentation = storePresentation;
exports.getBrowseCountry = getBrowseCountry;
exports.getBrowseCurrency = getBrowseCurrency;
exports.getBrowseLanguage = getBrowseLanguage;
exports.getBrowseSizeSchema = getBrowseSizeSchema;
exports.getFloor = getFloor;
exports.setFloor = setFloor;
exports.getKeyStoreDataversion = getKeyStoreDataversion;
exports.setKeyStoreDataversion = setKeyStoreDataversion;
exports.getPreferredSite = getPreferredSite;
exports.getSizeSchema = getSizeSchema;
exports.setSizeSchema = setSizeSchema;
exports.getTemplateVersion = getTemplateVersion;
exports.setTemplateVersion = setTemplateVersion;
exports.clearCookies = clearCookies;
exports.isFirstVisit = isFirstVisit;
exports.getCookieValue = getCookieValue;
exports.getGenderByFloorId = getGenderByFloorId;
exports.getFloorIdByGender = getFloorIdByGender;
exports.isMobile = isMobile;
exports.isTablet = isTablet;
exports.isStopMobileSet = isStopMobileSet;
exports.getPlatform = getPlatform;
exports.errors = errors;
